// useTabStore.js
import { create } from "zustand";
import {
  getTabsConfig,
  getDefaultTabs,
  getLoggedOutTabs,
} from "@/utils/tabsConfig";
import { defaultWidgetSizes, extractBaseId } from "@/utils/widgets";
import {
  breakpointCols,
  findFirstFitPosition,
  type BreakpointLayouts,
  type DashboardTab,
  type LayoutItem,
  type WidgetFilterData,
  type WidgetSearchData,
} from "@/utils/DashboardLayouts";
import { getUserTabs, saveUserTabs } from "@/shared/api/pref-storage";
import {
  readCachedTabs,
  writeCachedTabs,
} from "@/stores/tabLayoutCache";
import keycloakInstance from "@/initKeycloak";
import toast from "react-hot-toast";
import { debounce, isEqual } from "lodash";
import * as Sentry from "@sentry/react";
import { trackFeatureUsage } from "@/utils/gtm";
import { diagnosticEventLog } from "@/shared/services/diagnostic";

const hasInternalNewsquawkUserRole = () =>
  keycloakInstance.hasResourceRole("internal-newsquawk-user");

// Keycloak subject — scopes the local layout cache to this user.
const currentUserId = (): string | undefined =>
  keycloakInstance.tokenParsed?.sub;

export type { DashboardTab };

const defaultActiveTab = "default";

// Initial tabs for logged-out users; replaced with server data on auth success
let defaultTabs: DashboardTab[] = getLoggedOutTabs();

export type TabStore = {
  tabs: DashboardTab[];
  activeTabId: string;
  tabsLoaded: boolean;
  tabsLoadFailed: boolean;
  tabsFromCache: boolean;
  tabsSaveFailed: boolean;
  resetToDefaults: () => void;
  switchTab: (tabId: string) => void;
  addTab: (title: string) => void;
  importTab: (
    title: string,
    enabledWidgets: string[],
    layout: BreakpointLayouts,
    widgetFilters?: Record<string, WidgetFilterData>
  ) => void;
  removeTab: (tabId: string) => void;
  updateLayout: (tabId: string, newLayout: BreakpointLayouts) => void;
  addWidget: (tabId: string, widgetCompositeId: string) => void;
  removeWidget: (tabId: string, widgetCompositeId: string) => void;
  renameTab: (tabId: string, newTitle: string) => void;
  resetLayouts: (tabId: string) => void;
  setWidgetFilter: (
    widgetId: string,
    selections: Record<string, string[]>
  ) => void;
  setWidgetHighlight: (
    widgetId: string,
    selections: Record<string, string[]>
  ) => void;
  setWidgetSearch: (widgetId: string, search: WidgetSearchData) => void;
};

const useTabStore = create<TabStore>()((set) => ({
  tabs: defaultTabs,

  activeTabId: defaultActiveTab,

  tabsLoaded: false,

  tabsLoadFailed: false,

  tabsFromCache: false,

  tabsSaveFailed: false,

  // Actions
  resetToDefaults: () =>
    set({ tabs: defaultTabs, activeTabId: defaultActiveTab }),

  /**
   * Switch to a different tab
   */
  switchTab: (tabId: string) => set({ activeTabId: tabId }),

  /**
   * Add a new tab with a given title
   */
  addTab: (title: string) =>
    set((state) => {
      const newTabId = `tab-${Date.now()}`;

      const defaultLayout = getTabsConfig(hasInternalNewsquawkUserRole())
        .default.layout;

      const newTab = {
        id: newTabId,
        title,
        layout: defaultLayout,
        enabledWidgets: [],
      };
      return { tabs: [...state.tabs, newTab], activeTabId: newTabId };
    }),

  /**
   * Import a tab with a given title, widgets, layout, and optional widget filters
   */
  importTab: (
    title: string,
    enabledWidgets: string[],
    layout: BreakpointLayouts,
    widgetFilters?: Record<string, WidgetFilterData>
  ) =>
    set((state) => {
      const newTabId = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
      const newTab: DashboardTab = {
        id: newTabId,
        title,
        enabledWidgets,
        layout,
        ...(widgetFilters && { widgetFilters }),
      };
      return { tabs: [...state.tabs, newTab], activeTabId: newTabId };
    }),

  /**
   * Remove a tab by its ID
   */
  removeTab: (tabId: string) =>
    set((state) => ({
      tabs: state.tabs.filter((tab) => tab.id !== tabId),
      activeTabId: state.activeTabId === tabId ? "default" : state.activeTabId,
    })),

  /**
   * Update the layout of a specific tab
   */
  updateLayout: (tabId: string, newLayout: BreakpointLayouts) =>
    set((state) => ({
      tabs: state.tabs.map((tab) =>
        tab.id === tabId ? { ...tab, layout: newLayout } : tab
      ),
    })),

  addWidget: (tabId: string, widgetCompositeId: string) => {
    trackFeatureUsage({
      feature_name: "widget",
      feature_action: "add",
      feature_detail: extractBaseId(widgetCompositeId),
    });
    set((state) => {
      const widgetId = extractBaseId(widgetCompositeId);

      const updatedTabs = state.tabs.map((tab) => {
        if (tab.id !== tabId) return tab;

        const updatedEnabledWidgets = [
          ...(tab.enabledWidgets ?? []),
          widgetCompositeId,
        ];

        // Update the layout to include the new widget
        const updatedLayouts = { ...tab.layout };
        Object.keys(updatedLayouts).forEach((breakpoint) => {
          const layout =
            updatedLayouts[breakpoint as keyof typeof updatedLayouts] || [];

          if (!layout.find((item) => item.i === widgetCompositeId)) {
            const widgetSize =
              defaultWidgetSizes[widgetId as keyof typeof defaultWidgetSizes];
            if (!widgetSize) {
              console.error(
                `No default size found for widget "${widgetCompositeId}"`
              );
              return;
            }

            // Find optimal position using first-fit algorithm
            const cols = breakpointCols[breakpoint] || 16;
            const { x, y } = findFirstFitPosition(
              layout as LayoutItem[],
              widgetSize,
              cols
            );

            updatedLayouts[breakpoint as keyof typeof updatedLayouts] = [
              ...layout,
              { i: widgetCompositeId, ...widgetSize, x, y },
            ];
          }
        });

        return {
          ...tab,
          enabledWidgets: updatedEnabledWidgets,
          layout: updatedLayouts,
        };
      });

      return { tabs: updatedTabs };
    });
  },

  removeWidget: (tabId: string, widgetCompositeId: string) => {
    trackFeatureUsage({
      feature_name: "widget",
      feature_action: "remove",
      feature_detail: extractBaseId(widgetCompositeId),
    });
    set((state) => {
      const updatedTabs = state.tabs.map((tab) => {
        if (tab.id !== tabId) return tab;

        const updatedEnabledWidgets = tab.enabledWidgets?.filter(
          (id) => id !== widgetCompositeId
        );

        // Update the layout to remove the widget
        const updatedLayouts = { ...tab.layout };
        Object.keys(updatedLayouts).forEach((breakpoint) => {
          updatedLayouts[breakpoint as keyof typeof updatedLayouts] =
            updatedLayouts[breakpoint as keyof typeof updatedLayouts].filter(
              (item) => item.i !== widgetCompositeId
            );
        });

        // Clean up widget filter state. Demo-mode widgets persist under a
        // `demo-` prefixed key, so drop that alongside the live one.
        const remainingFilters = { ...tab.widgetFilters };
        delete remainingFilters[widgetCompositeId];
        delete remainingFilters[`demo-${widgetCompositeId}`];

        return {
          ...tab,
          enabledWidgets: updatedEnabledWidgets,
          layout: updatedLayouts,
          widgetFilters: Object.keys(remainingFilters).length > 0
            ? remainingFilters
            : undefined,
        };
      });

      return { tabs: updatedTabs };
    });
  },

  /**
   * Rename a user-created tab
   */
  renameTab: (tabId: string, newTitle: string) =>
    set((state) => ({
      tabs: state.tabs.map((tab) =>
        tab.id === tabId ? { ...tab, title: newTitle } : tab
      ),
    })),

  setWidgetFilter: (widgetId, selections) =>
    set((state) => ({
      tabs: state.tabs.map((tab) => {
        if (tab.id !== state.activeTabId) return tab;
        return {
          ...tab,
          widgetFilters: {
            ...tab.widgetFilters,
            [widgetId]: {
              ...tab.widgetFilters?.[widgetId],
              filter: selections,
            },
          },
        };
      }),
    })),

  setWidgetHighlight: (widgetId, selections) =>
    set((state) => ({
      tabs: state.tabs.map((tab) => {
        if (tab.id !== state.activeTabId) return tab;
        return {
          ...tab,
          widgetFilters: {
            ...tab.widgetFilters,
            [widgetId]: {
              ...tab.widgetFilters?.[widgetId],
              highlight: selections,
            },
          },
        };
      }),
    })),

  setWidgetSearch: (widgetId, search) =>
    set((state) => ({
      tabs: state.tabs.map((tab) => {
        if (tab.id !== state.activeTabId) return tab;
        return {
          ...tab,
          widgetFilters: {
            ...tab.widgetFilters,
            [widgetId]: {
              ...tab.widgetFilters?.[widgetId],
              search,
            },
          },
        };
      }),
    })),

  /**
   * Reset the layout and widget states of a specific tab to defaults
   */
  resetLayouts: (tabId: string) => {
    const tabsConfig = getTabsConfig(hasInternalNewsquawkUserRole());

    type TabConfigKey = keyof typeof tabsConfig;

    const config = tabsConfig[tabId as TabConfigKey];

    if (!config) return;

    set((state) => ({
      tabs: state.tabs.map((tab) => {
        if (tab.id !== tabId) return tab;

        return {
          ...tab,
          layout: config.layout,
          enabledWidgets: config.defaultWidgets,
        };
      }),
    }));
  },
}));

/**
 * The single writer for tab layouts.
 *
 * The local cache is refreshed only after the server acknowledges the write,
 * so it always mirrors server state rather than optimistic local edits. If the
 * save fails the cache is left alone — it keeps pointing at the last layout the
 * server actually confirmed.
 */
const persistTabs = async (tabs: DashboardTab[]) => {
  try {
    await saveUserTabs(tabs);
    writeCachedTabs(currentUserId(), tabs);
    if (useTabStore.getState().tabsSaveFailed) {
      useTabStore.setState({ tabsSaveFailed: false });
    }
  } catch (error) {
    // The pref-storage interceptor already logs and reports this. Swallow it
    // here so a failed save can't surface as an unhandled rejection, but flag
    // it so the user isn't left believing a change was stored.
    console.error("Error saving tabs to server:", error);
    useTabStore.setState({ tabsSaveFailed: true });
  }
};

const persistTabsDebounced = debounce(persistTabs, 3 * 1000, {
  leading: true,
});

/**
 * Save the current layout immediately, for the "retry now" affordance.
 *
 * Mostly a convenience: because every save PATCHes the whole layout, a failed
 * write is already repaired by the user's next edit. What it recovers is the
 * last change before they stop interacting, which otherwise sits unsaved.
 */
export const retrySaveTabs = async () => {
  const { tabs, tabsLoaded, tabsLoadFailed, tabsFromCache } =
    useTabStore.getState();

  // Same gate as the save subscription — a session that never read the
  // server's layout must not write, however the write was triggered.
  if (!tabsLoaded || tabsLoadFailed || tabsFromCache) return;

  // Drop any queued trailing-edge save so this doesn't double-write.
  persistTabsDebounced.cancel();
  await persistTabs(tabs);
};

// Subscribe to store changes and persist to server
// Only persist if store is initialized
// and only if the tabs state has changed
//
// This gate is load-bearing: until the server layout has been read successfully
// the store still holds `defaultTabs` (or a cached copy), and persisting those
// would overwrite the user's real saved layout in place (pref-storage has no
// history to recover it from). A failed read leaves tabsLoaded false, so each
// of these conditions alone is enough to block the write.
useTabStore.subscribe(async (state, prevState) => {
  if (!state.tabsLoaded || state.tabsLoadFailed || state.tabsFromCache) {
    return;
  }

  // persist state to db
  if (!isEqual(state.tabs, prevState.tabs)) {
    console.log("Tabs state changed, saving to server:", state.tabs);
    persistTabsDebounced(state.tabs);
  }
});

/**
 * One-time migration: reads widget filter/highlight selections from localStorage
 * and moves them into the tab store's widgetFilters field.
 * Skips widgets that already have filter state in the store (from server).
 * Removes migrated entries from localStorage.
 */
function migrateLocalStorageFilters(tabs: DashboardTab[]): DashboardTab[] {
  let migrated = false;
  const updatedTabs = tabs.map((tab) => {
    const widgetFilters = { ...tab.widgetFilters };
    for (const widgetId of tab.enabledWidgets) {
      // Skip if this widget already has filter state in the store
      if (widgetFilters[widgetId]) continue;

      const filterRaw = localStorage.getItem(widgetId);
      const highlightRaw = localStorage.getItem(`${widgetId}-highlight`);
      if (!filterRaw && !highlightRaw) continue;

      const entry: WidgetFilterData = {};
      try {
        if (filterRaw) entry.filter = JSON.parse(filterRaw);
      } catch {
        // invalid JSON — skip
      }
      try {
        if (highlightRaw) entry.highlight = JSON.parse(highlightRaw);
      } catch {
        // invalid JSON — skip
      }

      if (entry.filter || entry.highlight) {
        widgetFilters[widgetId] = entry;
        migrated = true;
      }
      // Remove from localStorage
      localStorage.removeItem(widgetId);
      localStorage.removeItem(`${widgetId}-highlight`);
    }
    return { ...tab, widgetFilters };
  });
  return migrated ? updatedTabs : tabs;
}

// Record tab/widget changes for diagnostic reports
useTabStore.subscribe((state, prevState) => {
  if (state.activeTabId !== prevState.activeTabId) {
    const tab = state.tabs.find((t) => t.id === state.activeTabId);
    diagnosticEventLog.record("tabs:widget", "info", "tab-switch", {
      tabId: state.activeTabId,
      widgetCount: tab?.enabledWidgets?.length ?? 0,
    });
  }

  if (!isEqual(state.tabs, prevState.tabs)) {
    const activeTab = state.tabs.find((t) => t.id === state.activeTabId);
    const prevActiveTab = prevState.tabs.find((t) => t.id === state.activeTabId);
    const currentWidgets = activeTab?.enabledWidgets ?? [];
    const prevWidgets = prevActiveTab?.enabledWidgets ?? [];

    if (!isEqual(currentWidgets, prevWidgets)) {
      diagnosticEventLog.record("tabs:widget", "info", "widgets-changed", {
        tabId: state.activeTabId,
        widgetCount: currentWidgets.length,
        widgets: currentWidgets,
      });
    }
  }
});

/**
 * Keep `activeTabId` pointing at a tab that actually exists.
 *
 * The store starts on `defaultActiveTab` and swapping in a freshly loaded set of
 * tabs never revisited it, so a layout without that id left the selection
 * dangling. `TabSystem` and `MobileTabDropdown` both paper over that by
 * displaying `tabs[0]`, but anything reading `activeTabId` directly — notably
 * the Manage Widgets panel, which renders nothing when it can't resolve the
 * active tab — was left pointing at a tab that isn't there.
 */
const resolveActiveTabId = (tabs: DashboardTab[]): string => {
  const { activeTabId } = useTabStore.getState();
  if (tabs.some((tab) => tab.id === activeTabId)) return activeTabId;
  return tabs[0]?.id ?? defaultActiveTab;
};

/**
 * Read the user's saved tab layout from the server.
 *
 * The store holds `defaultTabs` until this succeeds, and the save subscription
 * turns on as soon as `tabsLoaded` is true — so a failed read must never set
 * `tabsLoaded`. If it did, the next state change would persist the defaults
 * over the user's real layout, which pref-storage overwrites in place and
 * cannot restore.
 *
 * Exported so the dashboard can offer a retry instead of stranding the user on
 * defaults that look like their layout was wiped.
 *
 * On success the server response is mirrored to the local cache. On failure we
 * fall back to that cache for display only — the server always wins, and a
 * cached session never writes, so a layout edited on another machine can't be
 * clobbered by a stale local copy.
 */
export const loadUserTabs = async () => {
  useTabStore.setState({ tabsLoadFailed: false });

  try {
    const serverTabs: DashboardTab[] | undefined = await getUserTabs();
    if (serverTabs && serverTabs.length > 0) {
      // Migrate any widget filter selections from localStorage into the tab data.
      // Set tabs + tabsLoaded together so the subscription sees both changes
      // in one update — otherwise migrated filters won't be saved to server.
      const tabs = migrateLocalStorageFilters(serverTabs);
      useTabStore.setState({
        tabs,
        activeTabId: resolveActiveTabId(tabs),
        tabsLoaded: true,
        tabsLoadFailed: false,
        tabsFromCache: false,
      });
      // Server state wins unconditionally — refresh the mirror to match.
      writeCachedTabs(currentUserId(), tabs);
      toast.success("Tab layouts successfully loaded from server");
    } else {
      // No stored layout: a genuinely new user has nothing to overwrite, so
      // persisting the defaults from here is correct. Nothing to cache yet —
      // the first successful save will populate it.
      useTabStore.setState({
        tabsLoaded: true,
        tabsLoadFailed: false,
        tabsFromCache: false,
      });
    }
  } catch (error) {
    console.error("Error loading tabs from server:", error);
    Sentry.captureException(error);

    // Show the last server-confirmed layout if we have one. tabsLoaded stays
    // false either way — see the doc comment above — so nothing persists.
    const cachedTabs = readCachedTabs(currentUserId());
    if (cachedTabs) {
      useTabStore.setState({
        tabs: cachedTabs,
        activeTabId: resolveActiveTabId(cachedTabs),
        tabsLoadFailed: true,
        tabsFromCache: true,
      });
      toast.error("Couldn't reach the server — showing your last saved layout");
    } else {
      toast.error("Failed to load Tab layouts from server");
      useTabStore.setState({ tabsLoadFailed: true, tabsFromCache: false });
    }
  }
};

// Load initial tab state from server
// Only if Keycloak is authenticated
keycloakInstance.onAuthSuccess = async () => {
  // User has logged in,
  // Compute default tabs based on user role
  defaultTabs = getDefaultTabs(hasInternalNewsquawkUserRole());

  useTabStore.setState({
    tabs: defaultTabs,
  });

  await loadUserTabs();
};

export default useTabStore;
